import ManualBreadcrumb from '../../components/manual/ManualBreadcrumb';
import ManualSection from '../../components/manual/ManualSection';
import ManualAlert from '../../components/manual/ManualAlert';
import ManualStep from '../../components/manual/ManualStep';

function ManualPrimeiroAcessoPage() {
  return (
    <div className="manual-page">
      <ManualBreadcrumb items={[{ label: 'Primeiro Acesso' }]} />
      
      <div className="manual-header">
        <h1 className="manual-title">
          <i className="bi bi-key me-3"></i>
          Primeiro Acesso
        </h1>
        <p className="manual-subtitle">
          Aprenda a fazer login e dar os primeiros passos no sistema
        </p>
      </div>

      <ManualSection icon="bi-door-open" title="Como Fazer Login">
        <p>Para acessar o sistema, siga os passos abaixo:</p>
        
        <ManualStep number="1" title="Acesse a página inicial">
          Abra seu navegador (Google Chrome, Firefox ou Edge) e digite o endereço do sistema
          que foi fornecido pela equipe técnica da SEJUS.
        </ManualStep>

        <ManualStep number="2" title="Clique em 'Entrar'">
          Na página inicial, você verá um botão <strong>"Entrar"</strong>. Clique nele para acessar
          a tela de login.
        </ManualStep>

        <ManualStep number="3" title="Digite seu e-mail e senha">
          Preencha os campos:
          <ul>
            <li><strong>E-mail:</strong> O endereço de e-mail fornecido pelo administrador</li>
            <li><strong>Senha:</strong> Sua senha pessoal e confidencial</li>
          </ul>
        </ManualStep>

        <ManualStep number="4" title="Clique em 'Acessar'">
          Após preencher seus dados, clique no botão <strong>"Acessar"</strong> para entrar no sistema.
        </ManualStep>

        <ManualAlert type="tip" title="Dica">
          Se você é funcionário da SEJUS com e-mail institucional (@sejus.df.gov.br), o sistema
          usará automaticamente sua senha do Windows. Não precisa criar uma nova senha!
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-shield-lock" title="Esqueci Minha Senha">
        <p>Se você esqueceu sua senha, siga estas orientações:</p>
        
        <div className="manual-help-box">
          <h4><i className="bi bi-envelope me-2"></i>Usuários com e-mail da SEJUS</h4>
          <p>
            Se seu e-mail termina com <strong>@sejus.df.gov.br</strong>, sua senha é a mesma
            que você usa para entrar no computador. Entre em contato com o suporte de TI da
            SEJUS para recuperar ou alterar sua senha.
          </p>
        </div>

        <div className="manual-help-box">
          <h4><i className="bi bi-globe me-2"></i>Usuários de outros órgãos</h4>
          <p>
            Se você é de outro órgão (MPDFT, DPDF, etc.), entre em contato com o administrador
            do sistema na SEJUS pelo telefone ou e-mail fornecido na sua unidade.
          </p>
        </div>
      </ManualSection>

      <ManualSection icon="bi-eye" title="Conhecendo a Tela Inicial">
        <p>Após fazer login, você verá o <strong>Painel de Controle (Dashboard)</strong>:</p>
        
        <div className="manual-screen-guide">
          <div className="manual-screen-item">
            <div className="manual-screen-number">1</div>
            <div>
              <h5>Menu Lateral (esquerda) - Dinâmico</h5>
              <p>
                <strong>O menu muda automaticamente</strong> baseado no seu perfil e permissões.
                Você só verá os itens que tem permissão para acessar:
              </p>
              <ul>
                <li><i className="bi bi-speedometer2 me-2"></i><strong>Painel:</strong> Visão operacional das movimentações do dia</li>
                <li><i className="bi bi-people me-2"></i><strong>Adolescentes:</strong> Lista de adolescentes (se tiver <code>adolescentes.view</code>)</li>
                <li><i className="bi bi-table me-2"></i><strong>Tabelas de Apoio:</strong> Cadastros auxiliares exibidos conforme permissões <code>config.*</code></li>
                <li><i className="bi bi-pie-chart me-2"></i><strong>Relatórios:</strong> Gráficos e análises (se tiver <code>relatorios.view</code>)</li>
                <li><i className="bi bi-activity me-2"></i><strong>Logs do Sistema:</strong> Auditoria administrativa (se tiver <code>admin.logs</code>)</li>
                <li><i className="bi bi-book me-2"></i><strong>Manual do Usuário:</strong> Ajuda integrada do sistema</li>
              </ul>
            </div>
          </div>

          <div className="manual-screen-item">
            <div className="manual-screen-number">2</div>
            <div>
              <h5>Barra Superior (topo)</h5>
              <p>
                No canto direito da barra superior você encontra:
              </p>
              <ul>
                <li>
                  <i className="bi bi-sun-fill me-2"></i><strong>Alternar tema:</strong> Aparece nas telas
                  compatíveis. O dashboard tem preferência própria; as demais páginas do painel compatíveis
                  usam outra preferência.
                </li>
                <li><i className="bi bi-person-circle me-2"></i><strong>Seu Nome:</strong> Clique para acessar Minha Conta ou sair</li>
              </ul>
            </div>
          </div>

          <div className="manual-screen-item">
            <div className="manual-screen-number">3</div>
            <div>
              <h5>Área Central</h5>
              <p>
                É onde aparece o conteúdo das páginas. No painel inicial você verá:
              </p>
              <ul>
                <li>Filtros de status, data e busca por adolescente</li>
                <li>Totalizadores de efetivo, movimentações em aberto e finalizadas</li>
                <li>Movimentações agrupadas por meio de entrada, com cartões de atendimentos</li>
                <li>Informações atualizadas em tempo real via WebSocket</li>
              </ul>
            </div>
          </div>
        </div>

        <ManualAlert type="success" title="Atualização Automática de Permissões">
          O sistema usa <strong>WebSocket (Reverb)</strong> para atualizar suas permissões em tempo real.
          Se um administrador alterar suas permissões enquanto você está usando o sistema, o menu lateral
          será automaticamente atualizado para refletir as mudanças <strong>sem precisar fazer login novamente</strong>.
          Você verá uma mensagem no console do navegador quando isso acontecer.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-toggles" title="Personalizando sua Experiência">
        <h4>Recolhendo o Menu Lateral</h4>
        <p>
          Se você preferir mais espaço na tela, pode recolher o menu lateral clicando no ícone
          de <strong>três linhas</strong> <i className="bi bi-list"></i> no canto superior esquerdo.
          Clique novamente para expandir.
        </p>

        <h4 className="mt-4">Tema claro e tema escuro</h4>
        <p>
          Quando o botão de tema estiver disponível, você pode alternar entre tema claro e escuro.
          A preferência do <strong>dashboard</strong> é isolada da preferência do restante do painel,
          e ambas são salvas na sua conta quando o servidor aceitar a atualização.
        </p>
        
        <h4 className="mt-4">Navegando entre Páginas</h4>
        <p>
          Para voltar à página anterior, use o botão <strong>"Voltar"</strong> que aparece
          em algumas telas, ou clique diretamente no menu lateral para ir para outra seção.
        </p>
      </ManualSection>

      <ManualSection icon="bi-box-arrow-right" title="Como Sair do Sistema">
        <ManualStep number="1" title="Clique no seu nome">
          No canto superior direito da tela, clique no botão com o seu nome e o ícone de pessoa.
        </ManualStep>

        <ManualStep number="2" title="Selecione 'Sair'">
          No menu que aparece, clique em <strong>"Sair"</strong>. Você será desconectado e
          redirecionado para a página inicial.
        </ManualStep>

        <ManualAlert type="important" title="Importante">
          Sempre saia do sistema quando terminar de usar, especialmente se estiver em um
          computador compartilhado. Isso garante a segurança das informações.
        </ManualAlert>
      </ManualSection>

      <ManualSection icon="bi-exclamation-triangle" title="Problemas ao Fazer Login?">
        <div className="manual-troubleshoot">
          <h5><i className="bi bi-x-circle me-2"></i>Mensagem "E-mail ou senha incorretos"</h5>
          <p>
            Verifique se digitou corretamente seu e-mail e senha. Lembre-se que senhas diferenciam
            letras maiúsculas de minúsculas.
          </p>
        </div>

        <div className="manual-troubleshoot">
          <h5><i className="bi bi-x-circle me-2"></i>Página não carrega</h5>
          <p>
            Verifique sua conexão com a internet. Se o problema persistir, tente fechar e abrir
            o navegador novamente.
          </p>
        </div>

        <div className="manual-troubleshoot">
          <h5><i className="bi bi-x-circle me-2"></i>Sistema muito lento</h5>
          <p>
            Pode haver muitos acessos simultâneos. Aguarde alguns minutos e tente novamente.
            Se persistir, entre em contato com o suporte técnico.
          </p>
        </div>
      </ManualSection>

      <ManualAlert type="success" title="Pronto para Começar!">
        Agora que você já sabe fazer login e navegar pela tela inicial, explore os outros
        tópicos do manual para aprender a usar cada funcionalidade do sistema.
      </ManualAlert>
    </div>
  );
}

export default ManualPrimeiroAcessoPage;
